/**
 * File: apps/qbench-cp/reactapp/src/shared/hooks/misc/useDetectOutsideClick.js
 *
 * Purpose: Detects clicks outside a tracked element to drive an open/close active state, with
 *     special handling for nested menu containers.
 *
 * Scope: dropdown and menu components that need click-outside-to-close behavior.
 *
 * Tags: customer-portal
 *
 * Overview: Tracks an isActive boolean in state, initialized from initialState, and attaches a
 *     window click listener only while active. The click handler walks up the DOM to the
 *     nearest ".menu-container" ancestor for both the click target and the tracked element ref:
 *     it keeps the menu open when the click lands within the same menu-container, closes it
 *     when the click hits a different top-level menu-container, and closes it when the click is
 *     outside the tracked element entirely.
 *
 * Exports: useDetectOutsideClick hook returning [isActive, setIsActive].
 */

import { useState, useEffect, useCallback } from 'react';
import { getClosestParent } from 'shared/utils/misc';

/**
 * Hook for handling closing when clicking outside of an element
 * @param {React.node} el
 * @param {boolean} initialState
 */
export const useDetectOutsideClick = (el, initialState) => {
  const [isActive, setIsActive] = useState(initialState);

  const onClick = useCallback(
    (e) => {
      const targetParent = getClosestParent(e.target, '.menu-container');
      const elParent = getClosestParent(el.current, '.menu-container');

      if (targetParent && elParent && targetParent === elParent) {
        setIsActive(true);
      } else if (targetParent && targetParent.className === 'menu-container') {
        setIsActive(false);
      } else if (el.current !== null && !el.current.contains(e.target)) {
        setIsActive(false);
      }
    },
    [el, setIsActive],
  );
  useEffect(() => {
    // If the item is active (ie open) then listen for clicks outside
    if (isActive) {
      window.addEventListener('click', onClick);
    }

    return () => {
      window.removeEventListener('click', onClick);
    };
  }, [isActive, onClick]);

  return [isActive, setIsActive];
};
